import {
    HubConnection,
    HubConnectionBuilder,
    LogLevel,
    HttpTransportType,
    IHttpConnectionOptions,
} from "@aspnet/signalr";
import { app, alarm, enrollState, sensor } from "@/store";
import {
    AlarmStatus,
    ILocationFromParticle,
    IMiscInfo,
    IStatusChange,
    mapJsonToAlarmStatus,
} from "@/models/AlarmStatus";
import { configureOidc } from "@/store/services/security.service";
import { Log } from "./Logging";

let connectionObj: HubConnection | null = null;
let lastSensorsFetch = 0;
const SENSOR_REFRESH_MIN_MS = 5000; // refresh at most once per 5s

export function handleStatusChange(value: string, onAlarm = false) {
    if (value === "") {
        console.warn("Status value is empty, returning early.");
        return;
    }

    const statusChange: IStatusChange = JSON.parse(value);

    // Always apply age first (also clears per-device spinner once age != -1)
    sensor.SetSensorAge(statusChange);

    const senderDevice = sensor.devices?.find(
        (i) => i.attachedDeviceId === statusChange.device
    );

    if (onAlarm) {
        if (senderDevice) {
            alarm.setAlarmOrigin(senderDevice.attachedDeviceTypeId);
        }
        // if statusChange.status has last bit set to 1, always set alarm reason to 'alarm', if it has last bit 0, check statusChange.age is present and > 0, then set to 'age', but remember to parse age to number first
        let isAlarm = statusChange.status.endsWith("1");
        let isAged = statusChange.age && Number(statusChange.age) > 0;
        alarm.setAlarmReason(isAlarm ? "alarm" : isAged ? "age" : "");
        alarm.setAlarmModeTriggered(true);

        // Start/refresh 240s silence window on alarm events
        alarm.startSilenceWindow(alarm.sirenDuration);
        alarm.setSecondsSinceLastAlarm(0);
    }

    // Map status code to boolean "on/open"
    const isOnOrOpen = statusChange.status.endsWith("1");
    const isOffOrClosed = statusChange.status.endsWith("0");

    if (isOnOrOpen || isOffOrClosed) {
        const typeDescription = ((senderDevice as any)?.deviceTypeDescription || "").toString().toLowerCase();
        const isDoorOrHatch = typeDescription.includes("door") || typeDescription.includes("hatch") || typeDescription.includes("contact");
        const pairingTypeIdRaw = enrollState.EnrolledDeviceTypeId ?? enrollState.lastEnrolledDeviceTypeId;
        const pairingTypeId = pairingTypeIdRaw ? Number(pairingTypeIdRaw) : null;
        const pairingMatches =
            senderDevice &&
            pairingTypeId !== null &&
            !Number.isNaN(pairingTypeId) &&
            senderDevice.attachedDeviceTypeId === pairingTypeId;
        const hasPairingContext = pairingTypeId !== null && !Number.isNaN(pairingTypeId);
        const pairedAt = (senderDevice as any)?.pairedAt as number | undefined;
        const enrollAt = enrollState.lastEnrollAt ?? pairedAt;
        const recentAt = Math.max(enrollAt ?? 0, pairedAt ?? 0) || undefined;
        const DOOR_PAIRING_UNKNOWN_MS = 60 * 1000;
        const inPairingWindow = recentAt !== undefined && Date.now() - recentAt < DOOR_PAIRING_UNKNOWN_MS;
        const allowUnknown = inPairingWindow && (isDoorOrHatch || pairingMatches || (!senderDevice && hasPairingContext));
        const isInitialUnknown =
            statusChange.status === "0000" &&
            allowUnknown;
        const nextStatus = isOnOrOpen ? true : isInitialUnknown ? null : false;

        Log.information(
            `StatusChange debug: dev=${statusChange.device} status=${statusChange.status} next=${nextStatus} ` +
            `pairType=${pairingTypeIdRaw ?? ""} devTypeId=${(senderDevice as any)?.attachedDeviceTypeId ?? ""} ` +
            `typeDesc=${typeDescription} enrollAt=${enrollAt ?? ""} pairedAt=${pairedAt ?? ""} ` +
            `inWindow=${inPairingWindow} allowUnknown=${allowUnknown} pairingMatches=${pairingMatches}`
        );

        sensor.SetSensorStatus({
            device: statusChange.device,
            status: nextStatus,
        });
    } else {
        console.warn(
            `Unhandled status ${statusChange.status} for device ${statusChange.device}`
        );
    }

    refreshSensorsThrottled();
}

export async function configureMessageHub() {
    if (connectionObj) return Promise.resolve(connectionObj);

    const mainOidc = await configureOidc();
    const options: IHttpConnectionOptions = {
        accessTokenFactory: getAccessToken,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
    };

    const connection = new HubConnectionBuilder()
        .withUrl(`${mainOidc.appUrl}hubs/message`, options)
        .configureLogging(LogLevel.Debug)
        .build();

    // Simple toasts
    connection.on("SendNotificationToClient", (value) => {
        app.error = value;
    });

    // Arm/disarm
    connection.on("SetArmOrDisarmOnClient", (value) => {
        alarm.setArmed(value);
    });

    // Siren off
    connection.on("SetSirenOnClient", (value) => {
        alarm.setSiren(value);
    });

    // Alarm status payloads (keep location/voltage parsing if you need it later)
    connection.on("SetStatusOnClient", (value: string) => {
        if (value !== "") {
            const status: AlarmStatus = mapJsonToAlarmStatus(value);
            const i1 = parseFloat(
                status.VSupplies && status.VSupplies[0] && status.VSupplies[0].I1
                    ? status.VSupplies[0].I1
                    : "-999"
            );
            const v4 = parseFloat(
                status.VSupplies && status.VSupplies[0] && status.VSupplies[0].V4
                    ? status.VSupplies[0].V4
                    : "-999"
            );

            refreshSensorsThrottled();

            alarm.setPumpRunningState(i1 >= 0.75); // TODO: Fetch threshold from user settings once implemented
            // Connected if I1 is not -999 and a valid value
            alarm.setCurrentSensorConnected(i1 !== -999 && !isNaN(i1));

            // Set true only if V4 is a valid number and greater than 3
            alarm.setGasAlarmState(v4 > 3);
            // Connected if V4 is not -999 and a valid value
            alarm.setGasSensorConnected(v4 !== -999 && !isNaN(v4));

            status.fetchAt = new Date();
            alarm.setStatus(status);
        }
    });

    // Enrollment flow
    connection.on("JoinOpen", () => {
        Log.information(`SignalR: StartEnroll received`);
        enrollState.joinOpen();
    });
    connection.on("DeviceAdding", () => {
        Log.information(`SignalR: DeviceAdding received`);
        enrollState.startConfiguring();
    });
    connection.on("DeviceAdded", () => {
        Log.information("SignalR: DeviceAdded received, completing enrollment.");
        enrollState.success();
        refreshSensorsThrottled();
        // Battery data can lag behind pairing; do one delayed refresh to pick it up.
        refreshSensorsDelayed(4000);
    });
    connection.on("JoinClosed", () => {
        Log.information("SignalR: JoinClosed received, handling closed enrollment.");
        enrollState.handleJoinClosed();
    });
    connection.on("JoinFailed", () => {
        Log.information("SignalR: EnrollFailed received, failing enrollment.");
        enrollState.fail();
    });

    // Unpair/leave events -> refresh device list
    const handleDeviceLeave = (value?: string) => {
        Log.information(`SignalR: DeviceLeave received${value ? ` (${value})` : ""}`);
        refreshSensorsThrottled();
    };
    connection.on("DeviceLeft", () => {
        Log.information("SignalR: DeviceLeft received, refreshing sensors.");
        handleDeviceLeave();
    });

    // Position & misc
    connection.on("UpdatePosition", (value: string) => {
        if (value !== "") {
            const location: ILocationFromParticle = JSON.parse(value);
            alarm.setCurrentLocation(location);
        }
    });
    connection.on("UpdateMiscInfo", (value: string) => {
        if (value !== "") {
            const miscInfo: IMiscInfo = JSON.parse(value);
            alarm.setCurentMiscInfo(miscInfo);
        }
    });

    connection.on("UpdateStatusChange", handleStatusChange);
    connection.on("UpdateStatusChangeOnAlarm", (value: string) =>
        handleStatusChange(value, true)
    );
    connection.on("UpdateAgingDev", handleStatusChange);

    // Toasts
    connection.on("ShowMessage", (msg: string) => {
        if (msg !== "") app.success = msg;
    });
    connection.on("ShowError", (msg: string) => {
        if (msg !== "") app.error = msg;
    });

    connection.onclose(() => startConnection(connection));
    connectionObj = connection;
    await startConnection(connectionObj);
    return connectionObj;
}

const getAccessToken = async (): Promise<string> => {
    const mainOidc = await configureOidc();
    return mainOidc.accessToken;
};

const startConnection = async (connection: HubConnection) => {
    if (connection) {
        const startedPromise: Promise<void> = connection
            .start()
            .catch((err) => {
                Log.error("Failed to connect with hub", err);
                return new Promise((resolve, reject) =>
                    setTimeout(
                        () =>
                            startConnection(connection)
                                .then(() => resolve())
                                .catch(reject),
                        5000
                    )
                );
            });
        return startedPromise;
    }
};

function refreshSensorsThrottled() {
    const now = Date.now();
    if (now - lastSensorsFetch >= SENSOR_REFRESH_MIN_MS) {
        lastSensorsFetch = now;
        sensor.GetData();
    }
}

function refreshSensorsDelayed(delayMs: number) {
    window.setTimeout(() => refreshSensorsThrottled(), delayMs);
}